var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Page, SearchInput, SimplePagination, Table, Timestamp, } from '@dixa/lib-shared-habla';
import { SortDirection } from 'lib-shared-legacy-types';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMount, useSetState } from 'react-use';
import { analytics } from 'app/utils/segment';
import { IfAgentHasRole } from 'components/shared/agentRole';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import { withFlows, withWidgets } from 'hocs';
import { WigetSortKey } from 'hocs/widgets/api/types';
import { useNotifications } from 'hooks/useNotifications';
import { AddWidgetModal } from './addWidgetModal';
import { ChatWidgetDotsMenu } from './chatWidgetDotsMenu';
import { RenameWidgetModal } from './renameWidgetModal';
const COLUMN_COUNT = 3;
const FETCH_DELAY = 1600;
const ChatWidgetsImpl = (props) => {
    const navigate = useNavigate();
    const notify = useNotifications();
    const [state, setState] = useSetState({
        isConfirmModalOpen: false,
        isCreateModalOpen: false,
        isRenameModalOpen: false,
        selectedWidget: null,
        sortStatus: {
            direction: SortDirection.Ascending,
            sortBy: WigetSortKey.Name,
        },
    });
    useMount(() => {
        props.widgets.getItems();
    });
    const handleSearchSubmit = (searchTerm) => {
        var _a;
        const { widgets } = props;
        if (searchTerm !== ((_a = widgets.params) === null || _a === void 0 ? void 0 : _a.search)) {
            widgets.paramsChange({ search: searchTerm }, 0);
            analytics.chatSearchApplied();
        }
    };
    const handleSearchToggle = (isOpen) => {
        if (isOpen) {
            analytics.chatSearchOpened();
        }
        else {
            const { widgets } = props;
            analytics.chatSearchClosed();
            if (widgets.params.search) {
                widgets.paramsChange({ search: '' }, 0);
            }
        }
    };
    const handleSortChange = (newSortStatus) => {
        const { widgets } = props;
        const { sortStatus } = state;
        if (!isEqual(sortStatus, newSortStatus)) {
            setState({ sortStatus: newSortStatus });
            if (newSortStatus === null) {
                widgets.paramsChange({
                    sort_by: null,
                });
                return;
            }
            const { direction, sortBy } = newSortStatus;
            const sortByParam = `${sortBy}:${direction}`;
            widgets.paramsChange({
                sort_by: sortByParam,
            });
        }
    };
    const handleGoToWidgetSettings = (widgetId) => {
        navigate(`/settings/chat/${widgetId}`);
    };
    const handleGoToChatFlow = (flowId) => {
        navigate(`/settings/chat/flow/${flowId}`);
    };
    const handleGoToAuditLogs = (flowId) => {
        navigate(`/settings/chat/flow/${flowId}/history`);
    };
    const openCreateWidgetModal = () => {
        const { addChatWidgetClicked } = props;
        analytics.chatFormOpened({
            mode: 'create',
        });
        addChatWidgetClicked();
        setState({ isCreateModalOpen: true });
    };
    const closeCreateWidgetModal = () => {
        const { addChatWidgetClosed } = props;
        addChatWidgetClosed();
        setState({ isCreateModalOpen: false });
    };
    const openRenameWidgetModal = (widget) => {
        analytics.chatFormOpened({
            mode: 'update',
        });
        setState({ isRenameModalOpen: true, selectedWidget: widget });
    };
    const closeRenameWidgetModal = () => {
        setState({ isRenameModalOpen: false });
    };
    const handleDuplicateWidget = (widget) => __awaiter(void 0, void 0, void 0, function* () {
        const { flows, widgets } = props;
        const widgetCopyName = `${widget.name} (copy)`;
        const widgetCopy = Object.assign(Object.assign({}, widget), { name: widgetCopyName });
        const widgetFlowId = widget.flows[0].id;
        try {
            const [{ data: newWidget }, { data: widgetFlow }] = yield Promise.all([
                widgets.create(widgetCopy),
                flows.getItem(widgetFlowId),
            ]);
            const flowCopy = Object.assign(Object.assign({}, widgetFlow), { location: newWidget.id, name: newWidget.name, number: newWidget.id });
            const { data: newFlow } = yield flows.create(flowCopy);
            notify({
                title: 'Widget copied',
                text: `Widget was succesfully copied as "${widgetCopyName}"`,
                className: 'success',
                time: 5000,
                icon: 'done',
            });
            handleGoToChatFlow(newFlow.id);
        }
        catch (_a) {
            notify({
                title: 'An error occurred',
                text: `An error occurred when trying to copy the "${widget.name}" widget`,
                icon: 'exclamation',
                className: 'danger',
            });
        }
    });
    const openConfirmDeleteWidgetModal = (widget) => {
        setState({
            isConfirmModalOpen: true,
            selectedWidget: widget,
        });
    };
    const handleDeleteWidget = () => {
        const { widgets } = props;
        const { selectedWidget } = state;
        if (selectedWidget) {
            widgets
                .remove(selectedWidget.id)
                .then(() => {
                notify({
                    title: 'Widget deleted',
                    text: `"${selectedWidget.name}" widget was succesfully deleted`,
                    className: 'success',
                    time: 5000,
                    icon: 'done',
                });
                closeConfirmDeleteWidgetModal();
                // fetch widget items with delay to make sure
                // the new list is updated
                setTimeout(() => {
                    handleRefresh();
                }, FETCH_DELAY);
            })
                .catch(() => {
                closeConfirmDeleteWidgetModal();
                notify({
                    title: 'An error occurred',
                    text: `An error occurred when trying to delete widget "${selectedWidget.name}"`,
                    className: 'danger',
                    icon: 'exclamation',
                });
            });
        }
    };
    const closeConfirmDeleteWidgetModal = () => {
        setState({
            isConfirmModalOpen: false,
            selectedWidget: null,
        });
    };
    const goToPreviousPage = () => {
        analytics.chatPaginationPreviousClicked();
        props.widgets.previousPage();
    };
    const goToNextPage = () => {
        analytics.chatPaginationNextClicked();
        props.widgets.nextPage();
    };
    const handleRefresh = () => {
        props.widgets.getItems();
    };
    const renderLoadingStatus = () => {
        const { widgets } = props;
        if (widgets.getItemsAsyncStatus === 'failed') {
            return (_jsx(Table.Tr, { children: _jsxs(Table.Td, Object.assign({ colSpan: COLUMN_COUNT }, { children: ["Something went wrong...", _jsx(Button, Object.assign({ variant: "tertiary", onClick: handleRefresh }, { children: "Try again" }))] })) }));
        }
        return _jsx(TableLoadingRow, { colSpan: COLUMN_COUNT });
    };
    const renderRow = (widget) => {
        return (_jsxs(Table.Tr, { children: [_jsx(Table.Td, { children: widget.name }), _jsx(Table.Td, { children: widget.updated_at ? (_jsx(Timestamp, { relative: true, time: widget.updated_at })) : ('-') }), _jsx(Table.Td, { children: _jsx(ChatWidgetDotsMenu, { deleteWidget: openConfirmDeleteWidgetModal, duplicateWidget: handleDuplicateWidget, goToChatFlow: handleGoToChatFlow, goToAuditLogs: handleGoToAuditLogs, goToWidgetSettings: handleGoToWidgetSettings, renameWidget: openRenameWidgetModal, widget: widget }) })] }, widget.id));
    };
    const renderBody = () => {
        const { widgets } = props;
        const { getItemsAsyncStatus, items } = widgets;
        if (getItemsAsyncStatus !== 'succeeded') {
            return renderLoadingStatus();
        }
        if (items.length) {
            return items.map(renderRow);
        }
        return (_jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: COLUMN_COUNT }, { children: "No widgets found." })) }));
    };
    const renderFoot = () => {
        const { widgets } = props;
        const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = widgets;
        const hasFoot = hasNextPage || hasPreviousPage;
        if (!hasFoot)
            return null;
        return (_jsx(Table.Tfoot, { children: _jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: COLUMN_COUNT }, { children: _jsx(SimplePagination, { previousButtonClick: goToPreviousPage, previousButtonDisabled: !hasPreviousPage, nextButtonClick: goToNextPage, nextButtonDisabled: !hasNextPage, loading: getItemsAsyncStatus === 'loading' }) })) }) }));
    };
    const { isConfirmModalOpen, isCreateModalOpen, isRenameModalOpen, selectedWidget, sortStatus, } = state;
    return (_jsxs(_Fragment, { children: [_jsx(Page.Section, Object.assign({ title: "All widgets", controls: _jsxs(_Fragment, { children: [_jsx(SearchInput, { onToggle: handleSearchToggle, onSubmit: handleSearchSubmit }), _jsx(IfAgentHasRole, Object.assign({ role: 'admin' }, { children: _jsx(Button, Object.assign({ onClick: openCreateWidgetModal, id: "add-chat-widget-btn" }, { children: "Add widget" })) }))] }) }, { children: _jsxs(Table, Object.assign({ framed: true, stickyHeader: true, stickyFooter: true, headerWithBackground: true, sortingProps: {
                        onSortChange: handleSortChange,
                        sortStatus,
                    } }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ columnId: WigetSortKey.Name }, { children: "Name" })), _jsx(Table.Th, Object.assign({ columnId: WigetSortKey.UpdatedAt }, { children: "Updated" })), _jsx(Table.Th, { columnId: null })] }) }), _jsx(Table.Tbody, { children: renderBody() }), renderFoot()] })) })), _jsx(AddWidgetModal, { goToFlow: handleGoToChatFlow, isOpen: isCreateModalOpen, onClose: closeCreateWidgetModal }), selectedWidget && (_jsx(RenameWidgetModal, { initialValues: { name: selectedWidget.name }, isOpen: isRenameModalOpen, onClose: closeRenameWidgetModal, refreshPage: handleRefresh, widget: selectedWidget })), _jsx(ConfirmModal, { cancel: closeConfirmDeleteWidgetModal, cancelText: "Cancel", confirm: handleDeleteWidget, confirmText: "Yes, delete it now", headline: "Are you sure?", isShown: isConfirmModalOpen, text: `Are you sure you want to delete the widget
            "${selectedWidget === null || selectedWidget === void 0 ? void 0 : selectedWidget.name}"?` })] }));
};
const mapDispatchToProps = (dispatch) => ({
    addChatWidgetClicked: () => dispatch({ type: 'ADD_CHAT_WIDGET_CLICKED' }),
    addChatWidgetClosed: () => dispatch({ type: 'ADD_CHAT_WIDGET_CLOSED' }),
});
export const ChatWidgets = connect(null, mapDispatchToProps)(withWidgets(withFlows(ChatWidgetsImpl), {
    initialParams: {
        sort_by: `${WigetSortKey.Name}:asc`,
    },
}));
